/* What the four tabs of the Machine wise page share: the colour bands, the
 * pattern tag, the sparkline, and the sentence each pattern is explained in. */

import type { MachineRow, MachineSummary, Pattern, Shift } from '../../lib/api_machines'
import { SHIFTS } from '../../lib/api_machines'
import { num } from '../../lib/format'

/** What every tab needs to know about the month in view. */
export interface Ctx {
  data: MachineSummary
  bench: number
  benchLabel: string
  /** 'Sep' and 'September 2026' */
  monShort: string
  monLong: string
  /** Day of month for each date in `data.dates`. */
  days: string[]
  open: (code: string) => void
}

export const R: React.CSSProperties = { textAlign: 'right' }

export const dayNum = (iso: string) => String(Number(iso.slice(8, 10)))
export const unitName = (u: number) => `Unit ${u}`

// ------------------------------------------------------------------ colour
/* The design's `pal()`, as tokens. `low` is the below-benchmark band and is
   deliberately the highest-contrast of the three — see --eff-weak in ds.css. */
export const BAND = {
  ok: 'var(--c-bar1)', mid: 'var(--c-bar2)', warn: 'var(--c-bar4)', low: 'var(--eff-weak)',
}

/** Under the benchmark, under standard, or at it. */
export const bandFill = (v: number, bench: number) =>
  v < bench ? BAND.low : v < 100 ? BAND.mid : BAND.ok

export const SHIFT_COLOR: Record<Shift, string> = {
  A: 'var(--c-shA)', B: 'var(--c-shB)', C: 'var(--c-shC)',
}

/* Six bar colours for up to eight stages, so the last ones are told apart
   by dash. The dark theme has only four distinct bar colours, which is why
   the dashes start at the fifth stage rather than the seventh. */
const STAGE_COLORS = [
  'var(--c-bar1)', 'var(--c-bar3)', 'var(--c-bar2)',
  'var(--c-bar4)', 'var(--c-bar5)', 'var(--c-bar6)',
]
export const stageColor = (k: number) => STAGE_COLORS[k % STAGE_COLORS.length]
export const stageDash = (k: number) => (k < 4 ? undefined : k < 6 ? '5 3' : '2 3')

// --------------------------------------------------------------------- tag
const TAG: Record<Pattern, string> = {
  'On standard': 'tag-neutral',
  'Standard suspect': 'tag-outline',
  'Shift-linked': 'tag-accent-2',
  'Erratic': 'tag-accent',
  'Chronic low': 'tag-accent',
  'Declining': 'tag-accent-2',
  'Marginal': 'tag-outline',
}

export function PatternTag({ pattern }: { pattern: Pattern }) {
  return <span className={`tag ${TAG[pattern]}`} style={{ whiteSpace: 'nowrap' }}>{pattern}</span>
}

// -------------------------------------------------------------- small marks
/** A value in a track — utilisation, average efficiency. */
export function Meter({ width, value, max, fill }: {
  width: number; value: number; max: number; fill: string
}) {
  return (
    <svg viewBox={`0 0 ${width} 8`} style={{ width, height: 8, display: 'block', flex: 'none' }}>
      <rect x="0" y="1" width={width} height="6" fill="var(--c-track)" />
      <rect x="0" y="1" height="6" fill={fill}
        width={Math.max(1, Math.min(width, (value / max) * width))} />
    </svg>
  )
}

/** A machine's day efficiencies across the month, on the 40–120 scale,
 *  with the benchmark dashed behind them. */
export function Spark({ m, dates, bench, height, pixels, stroke = 1.5 }: {
  m: MachineRow
  dates: string[]
  bench: number
  /** viewBox height, and the height it is drawn at. */
  height: number
  pixels: number
  stroke?: number
}) {
  const y = (eff: number) => height - Math.max(0, Math.min(1, (eff - 40) / 80)) * height
  const at = new Map(dates.map((d, i) => [d, i]))
  const points = m.days.map((d) => {
    const x = dates.length > 1 ? ((at.get(d.date) ?? 0) / (dates.length - 1)) * 100 : 50
    return `${x.toFixed(1)},${y(d.eff).toFixed(1)}`
  }).join(' ')
  return (
    <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none"
      style={{ width: '100%', height: pixels, display: 'block' }}>
      <line x1="0" y1={y(bench)} x2="100" y2={y(bench)} stroke="var(--c-grid)"
        strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
      <polyline points={points} fill="none" stroke="var(--c-bar1)" strokeWidth={stroke}
        vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

// ------------------------------------------------------------------- story
const f1 = (v: number | null | undefined) => num(v, 1)
const f0 = (v: number | null | undefined) => num(v, 0)

/** The paragraph that says what a machine's pattern means and what to do
 *  about it. The wording is the design's; the figures are the machine's. */
export function story(m: MachineRow, c: Ctx): string {
  const ran = SHIFTS.filter((s) => m.shifts[s] !== null)
  const byEff = [...ran].sort((a, b) => (m.shifts[a] as number) - (m.shifts[b] as number))
  const worstShift = byEff[0]
  const bestShift = byEff[byEff.length - 1]
  const stageMean = m.stage_mean
  const lineWide = stageMean !== null && stageMean < c.bench && Math.abs(stageMean - m.mean) < 6
  const on = (iso: string) => `${dayNum(iso)} ${c.monShort}`

  switch (m.pattern) {
    case 'Standard suspect':
      return `Averages ${f1(m.mean)}% of its standard and never really drops below it, so the standard on this machine is almost certainly set too low. Re-rate it before comparing this machine with the rest of the line.`
    case 'Shift-linked':
      return `Shift ${worstShift} averages ${f1(m.shifts[worstShift])}% against shift ${bestShift}’s ${f1(m.shifts[bestShift])}% — a ${f1(m.spread)} point gap on the same machine and the same count. The machine can clearly do the higher number, so look at manning, doffing discipline and handover on shift ${worstShift}.`
    case 'Erratic':
      return `Swings from ${f0(m.worst.eff)}% on ${on(m.worst.date)} to ${f0(m.best.eff)}% on ${on(m.best.date)} (±${f1(m.sd)}). Losses arrive in bursts rather than steadily — pull the stoppage entries for its ${m.below} weak days; this reads as breakdown or material-starvation, not a capability limit.`
        + weakDays(m, c)
    case 'Chronic low':
      return lineWide
        ? `Sits at ${f1(m.mean)}% with a spread of only ±${f1(m.sd)}, and the whole ${m.stage.toLowerCase()} line in ${unitName(m.unit).toLowerCase()} averages ${f1(stageMean)}% — so this is a line-level gap, not one bad machine. Either the standard was set for a different count or speed, or the stage is genuinely being starved. Settle the standard first; a stage this flat has nothing day-to-day to fix.`
        : `Sits at ${f1(m.mean)}% every single day with a spread of only ±${f1(m.sd)} — the flattest kind of loss, and below its own stage average of ${f1(stageMean ?? m.mean)}%. A machine this steady is not being stopped, it is running slow: check speed setting, drafting, and whether the standard reflects the count it actually runs.`
    case 'Declining':
      return `Started the month around ${f1(m.first)}% and finished around ${f1(m.last)}%. Nothing dramatic on any one day, which usually points at a drifting mechanical condition — worth a check before it settles at the lower level.`
    case 'Marginal':
      return `At ${f1(m.mean)}% it is just under the ${c.benchLabel} benchmark, below it on ${m.below} of ${m.n} days. Small, recoverable gap.`
    default:
      return `Runs at ${f1(m.mean)}% with a spread of ±${f1(m.sd)} — on standard and steady. Use it as the reference for the rest of the stage.`
  }
}

/** What the register holds against an erratic machine on its weak days.
 *
 *  The design could only tell the reader to go and pull those entries: its
 *  register was kept per unit. The tracker books stoppages per machine and
 *  shift, so the page can say what they add up to — or that there are none,
 *  which is its own finding. */
function weakDays(m: MachineRow, c: Ctx): string {
  if (!c.data.register || !m.below) return ''
  return m.weak_stop_min > 0
    ? ` The register books ${f0(m.weak_stop_min)} minutes against it on those days, most of it as “${m.weak_top ?? ''}”.`
    : ' The register books nothing against it on those days, so whatever is stopping it is not being written down.'
}
